/* eslint-disable no-underscore-dangle */
/* eslint-disable no-param-reassign */
import { cloneDeep } from 'lodash-es';

/**
 * Undo-Redo Vuex plugin with dynamically registered modules (Vue 3 / Vuex 4 compatible).
 *
 * @param {object} store - The Vuex store instance.
 * @param {string[]} namespacedModulesToRecord - An array of namespaced module names
 * to apply undo-redo functionality.
 */
const undoRedoPlugin = (store, namespacedModulesToRecord) => {
  let currentIndex = 0;
  let debouncedTimeout;
  let pendingMutation;
  let isApplying = false; // Lock flag to prevent recording mutations caused by undo/redo

  /**
   * Records a Vuex mutation to the history for tracking.
   *
   * @function recordMutation
   * @param {Object} mutation - The mutation that was performed.
   */
  const recordMutation = (mutation) => {
    if (!store.state.history) {
      store.state.history = [];
    }

    // Discard future history if undoing from an intermediate state
    if (store.state.currentSavedStateIndex > currentIndex) {
      // The saved snapshot is on the branch being discarded, not the new branch.
      store.state.currentSavedStateIndex = -1;
    }
    store.state.history.splice(currentIndex + 1);
    currentIndex += 1;
    store.state.currentIndex = currentIndex;

    // Clone ONLY the monitored module subtrees (prevents cloning history inside history)
    const stateToRecord = {};
    namespacedModulesToRecord.forEach((namespace) => {
      if (store.state[namespace]) {
        stateToRecord[namespace] = cloneDeep(store.state[namespace]);
      }
    });

    store.state.history.push({ mutation, state: stateToRecord });
  };

  /**
   * Checks if a mutation is blacklisted based on its payload.
   *
   * @function isMutationBlacklisted
   * @param {object} mutation - The Vuex mutation object.
   * @returns {boolean} Whether the mutation is blacklisted.
   */
  const isMutationBlacklisted = (mutation) => mutation.payload?.options?.blacklist
    || mutation.type === 'graphicsTemplate/SET_MEDIA_SIZE_SUMMARY';

  /**
   * Records a pending edit before changing the history position.
   *
   * @function flushPendingMutation
   */
  const flushPendingMutation = () => {
    clearTimeout(debouncedTimeout);
    debouncedTimeout = undefined;
    if (pendingMutation) {
      const mutation = pendingMutation;
      pendingMutation = undefined;
      recordMutation(mutation);
    }
  };

  // Coalesce rapid edits, but keep them available for an immediate undo/redo.
  const debounceMutation = (mutation) => {
    clearTimeout(debouncedTimeout);
    pendingMutation = mutation;
    debouncedTimeout = setTimeout(flushPendingMutation, 300);
  };

  /**
   * Checks if a mutation comes from a namespaced module included in the allowed list.
   *
   * @function shouldRecordMutation
   * @param {object} mutation - The Vuex mutation object.
   * @returns {boolean} Whether the mutation comes from an allowed namespaced module.
   */
  const shouldRecordMutation = (mutation) => namespacedModulesToRecord.includes(mutation.type.split('/')[0]);

  store.subscribe((mutation) => {
    // Prevent undo/redo state restoration from recording new history steps
    if (isApplying) return;

    if (shouldRecordMutation(mutation)) {
      if (isMutationBlacklisted(mutation)) {
        return;
      }

      debounceMutation(mutation);
    }
  });

  /**
   * Restores module state in-place to preserve Vue 3 Proxy references.
   *
   * @function applyState
   * @param {Object} historyState - The target snapshot from the history array.
   */
  const applyState = (historyState) => {
    isApplying = true;

    try {
      const { currentSavedStateIndex, ...restState } = cloneDeep(historyState);

      namespacedModulesToRecord.forEach((namespace) => {
        const target = store.state[namespace];
        const source = restState[namespace];

        if (target && source) {
          // 1. Remove keys that were added after this history point
          Object.keys(target).forEach((key) => {
            if (!(key in source)) {
              delete target[key];
            }
          });

          // 2. Mutate the existing reactive module proxy in-place
          Object.assign(target, source);
        }
      });
    } finally {
      isApplying = false;
    }
  };

  /**
   * Sets the initial state of the Vuex store for history tracking.
   */
  store.setInitialState = () => {
    clearTimeout(debouncedTimeout);
    debouncedTimeout = undefined;
    pendingMutation = undefined;
    currentIndex = 0;
    store.state.currentIndex = 0;
    store.state.currentSavedStateIndex = 0;
    store.state.history = [];

    const _stateToRecord = {};
    namespacedModulesToRecord.forEach((namespace) => {
      if (store.state[namespace]) {
        _stateToRecord[namespace] = cloneDeep(store.state[namespace]);
      }
    });

    store.state.history.push({ mutation: 'init', state: _stateToRecord });
  };

  /**
   * Performs an undo operation by reverting to the previous state in the history.
   */
  store.undo = () => {
    flushPendingMutation();
    if (store.state.currentIndex > 0) {
      currentIndex -= 1;
      store.state.currentIndex -= 1;

      const { state: historyState } = store.state.history[currentIndex];
      applyState(historyState);
    }
  };

  /**
   * Performs a redo operation by moving forward to the next state in the history.
   */
  store.redo = () => {
    flushPendingMutation();
    if (store.state.currentIndex < store.state.history.length - 1) {
      currentIndex += 1;
      store.state.currentIndex += 1;

      const { state: historyState } = store.state.history[currentIndex];
      applyState(historyState);
    }
  };
};

export default undoRedoPlugin;
